import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Stack,
  Typography,
} from "@mui/material";
import StatusChip from "./StatusChip";
import { formatDateTime } from "../utils/firewallFormatters";

function Field({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ mt: 0.25, wordBreak: "break-word" }}>{value || "—"}</Typography>
    </Box>
  );
}

export default function FirewallDetailsDrawer({
  open,
  firewall,
  canManage,
  onClose,
  onEdit,
  onAssign,
  onEditAssignment,
  onUnassign,
  onRemoveAssignment,
}) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 540 } } }}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>{firewall?.device_name || "Sophos firewall"}</Typography>
            <Typography variant="body2" color="text.secondary">{firewall?.device_serial_id}</Typography>
          </Box>
          <StatusChip active={Boolean(firewall?.is_active)} />
        </Stack>

        {canManage && (
          <Stack direction="row" gap={1} sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" onClick={onEdit}>Edit</Button>
            <Button size="small" variant="contained" onClick={onAssign}>Assign shops</Button>
          </Stack>
        )}

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Field label="Device model" value={firewall?.device_model} />
          <Field label="Firmware" value={firewall?.firmware_version} />
          <Field label="Public IP" value={firewall?.last_public_ip} />
          <Field label="Location" value={firewall?.location_name} />
          <Field label="First seen" value={formatDateTime(firewall?.first_seen_at)} />
          <Field label="Last seen" value={formatDateTime(firewall?.last_seen_at)} />
          <Field label="Traffic events" value={firewall?.traffic_events_count ?? 0} />
          <Field label="Active shops" value={firewall?.active_shops_count ?? 0} />
        </Box>

        <Box sx={{ mt: 2 }}><Field label="Notes" value={firewall?.notes} /></Box>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" fontWeight={700}>Shop assignments</Typography>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {(firewall?.shops || []).map((shop) => (
            <Box key={shop.id} sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" gap={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>{shop.shop_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{shop.shop_code} · {shop.brand?.name || "No brand"}</Typography>
                </Box>
                <Chip size="small" label={shop.pivot?.connection_role || "shared"} />
              </Stack>
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                <StatusChip active={shop.pivot?.is_active ?? true} activeLabel="Assignment active" inactiveLabel="Assignment inactive" />
                {shop.pivot?.is_primary && <Chip size="small" color="primary" variant="outlined" label="Primary" />}
              </Stack>
              {canManage && (
                <Stack direction="row" gap={1} sx={{ mt: 1.5 }}>
                  <Button size="small" onClick={() => onEditAssignment(shop)}>Edit</Button>
                  <Button size="small" color="warning" onClick={() => onUnassign(shop)}>Unassign</Button>
                  <Button size="small" color="error" onClick={() => onRemoveAssignment(shop)}>Remove</Button>
                </Stack>
              )}
            </Box>
          ))}
          {!firewall?.shops?.length && <Typography variant="body2" color="text.secondary">No shops are assigned.</Typography>}
        </Stack>
      </Box>
    </Drawer>
  );
}
